// EditCommunicationLogModal.tsx
import React, { useEffect, useState, useRef } from "react";
import { Formik, Field, ErrorMessage } from "formik";
import { Modal, Form, Button, Row, Col, Stack } from "react-bootstrap";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import Select from "react-select";
import { showToastSuccess, showToastError } from "../../helpers";
import { communicationLogService } from "../../services/communicationlog.service";
import { customerService, leadService, documentService } from "../../services";
import { DocumentLibrary } from "../../constants/DocumentLibraryFolderId";
import { LoadingSpinner } from "../LoadingSpinner";
import { ReactSelectBootstrapStyle } from "../../styles/ReactSelectBootstrapStyle";
import { OptionType } from "../FormikSelect";
import { ICommunicationLog } from "@/customTypings/CommunicationLog";

interface EditCommunicationLogModalProps {
  show: boolean;
  onClose: (updated: boolean) => void;
  item: ICommunicationLog;
}

export const EditCommunicationLogModal: React.FC<EditCommunicationLogModalProps> = ({ show, onClose, item }) => {
  const [alertVariant, setAlertVariant] = useState<string>("danger");
  const [documents, setDocuments] = useState<any[]>([]);
  const [allCustomers, setAllCustomers] = useState<OptionType[]>([]);
  const [allLeads, setAllLeads] = useState<OptionType[]>([]);
  const [customerId, setCustomerId] = useState<string>(item.customerId || "");
  const [leadId, setLeadId] = useState<string>(item.leadId || "");
  const [isUploadingFiles, setIsUploadingFiles] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    customerService.getAllListItems().then((response) => {
      const custOptions = Array.isArray(response) ? response : [];
      setAllCustomers(custOptions);
    });
    leadService.getAllListItems().then((response) => {
      const leadOptions = Array.isArray(response) ? response : [];
      setAllLeads(leadOptions);
    });
    documentService.getDocuments(DocumentLibrary.folderId).then((res: any) => {
      const docs = res.results.filter((d: any) => d.entityId === item.id);
      setDocuments(docs);
    });
    setCustomerId(item.customerId || "");
    setLeadId(item.leadId || "");
  }, [item]);

  const isCustomerEmpty = !item.customerId || item.customerId === "00000000-0000-0000-0000-000000000000";
  const initialRegarding = isCustomerEmpty ? "Lead" : "Customer";

  const initialValues = {
    ...item,
    date: item.date ? new Date(item.date).toISOString().split("T")[0] : "",
    files: item.files ? item.files : [],
    regarding: initialRegarding,
  };

  const validationSchema = Yup.object().shape({
    date: Yup.date().required("Date is required"),
    regarding: Yup.string().required("Regarding is required"),
    customerId: Yup.string().when("regarding", {
      is: "Customer",
      then: Yup.string().required("Customer is required"),
      otherwise: Yup.string().nullable(),
    }),
    leadId: Yup.string().when("regarding", {
      is: "Lead",
      then: Yup.string().required("Lead is required"),
      otherwise: Yup.string().nullable(),
    }),
    from: Yup.string().required("From is required"),
    to: Yup.string().required("To is required"),
    subject: Yup.string().required("Subject is required"),
    body: Yup.string().required("Body is required"),
    files: Yup.array().of(Yup.mixed()).default([]),
  });

  const handleRegardingChange = (
    value: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<any>
  ) => {
    setFieldValue("regarding", value);
    if (value === "Customer") {
      setFieldValue("leadId", "");
      setLeadId("");
    } else {
      setFieldValue("customerId", "");
      setCustomerId("");
    }
  };

  const handleFileUpload = (communicationLogId: string, files: File[]) => {
    setIsUploadingFiles(true);
    documentService.uploadFile(DocumentLibrary.folderId, files, communicationLogId)
      .then(() => {
        documentService.getDocuments(DocumentLibrary.folderId).then((res: any) => {
          const docs = res.results.filter((d: any) => d.entityId === communicationLogId);
          setDocuments(docs);
          setIsUploadingFiles(false);
        });
      })
      .catch(() => {
        showToastError("Error uploading files");
        setIsUploadingFiles(false);
      });
  };

  const handleFileDelete = (docId: string) => {
    documentService.removeDocument(docId)
      .then(() => {
        documentService.getDocuments(DocumentLibrary.folderId).then((res: any) => {
          const docs = res.results.filter((d: any) => d.entityId === item.id);
          setDocuments(docs);
        });
      })
      .catch(() => showToastError("Error deleting file"));
  };

  const renderDocumentPreview = (doc: any) => (
    <div key={doc.id} style={{ display: "flex", alignItems: "center", marginBottom: "0.5rem" }}>
      <Button variant="link"
        onClick={() =>
          window.open(`/api/document/download/${doc.id}`, "_blank", "noopener,noreferrer")
        }
      >
        {doc.fileName}
      </Button>
      <Button variant="danger" size="sm" onClick={() => handleFileDelete(doc.id)}>
        Delete
      </Button>
    </div>
  );

  return (
    <Modal show={show} onHide={() => onClose(false)} centered size="lg" dialogClassName="communication-modal">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setStatus, setSubmitting }) => {
          const { regarding, ...rest } = values;
          const payload: ICommunicationLog = {
            ...rest,
            customerId: rest.customerId && rest.customerId.trim() !== "" ? rest.customerId : undefined,
            leadId: rest.leadId && rest.leadId.trim() !== "" ? rest.leadId : undefined,
            date: new Date(rest.date),
          };

          communicationLogService.update(payload).then(
            (response) => {
              setSubmitting(false);
              if (response.status !== "Failure") {
                showToastSuccess("Communication Log Updated Successfully");
                onClose(true);
              } else {
                setAlertVariant("danger");
                setStatus(response.message);
              }
            },
            (error) => {
              setAlertVariant("danger");
              setStatus(error);
              setSubmitting(false);
            }
          );
        }}
      >
        {({ values, errors, touched, isSubmitting, handleSubmit, handleChange, setFieldValue, setStatus }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>Edit Communication Log</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Stack gap={3}>
                <Form.Group as={Row} controlId="date">
                  <Form.Label column xs={3}>Date *</Form.Label>
                  <Col xs={9}>
                    <Field name="date" type="date" className="form-control" />
                    <ErrorMessage name="date" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="regarding">
                  <Form.Label column xs={3}>Regarding *</Form.Label>
                  <Col xs={9}>
                    <Field
                      as="select"
                      name="regarding"
                      className="form-control"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        handleRegardingChange(e.target.value, setFieldValue)
                      }
                    >
                      <option value="Customer">Customer</option>
                      <option value="Lead">Lead</option>
                    </Field>
                    <ErrorMessage name="regarding" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                {values.regarding === "Customer" ? (
                  <Form.Group as={Row} controlId="customerId" className="mb-3">
                    <Form.Label column xs={3}>Customer *</Form.Label>
                    <Col xs={9}>
                      <Select
                        styles={ReactSelectBootstrapStyle}
                        options={allCustomers}
                        isClearable
                        onChange={(option: OptionType | null) =>
                          setFieldValue("customerId", option ? option.value : "")
                        }
                        value={
                          allCustomers.find((opt: OptionType) => opt.value === values.customerId) || null
                        }
                      />
                      <ErrorMessage name="customerId" component="div" className="invalid-feedback" />
                    </Col>
                  </Form.Group>
                ) : (
                  <Form.Group as={Row} controlId="leadId" className="mb-3">
                    <Form.Label column xs={3}>Lead *</Form.Label>
                    <Col xs={9}>
                      <Select
                        styles={ReactSelectBootstrapStyle}
                        options={allLeads}
                        isClearable
                        onChange={(option: OptionType | null) =>
                          setFieldValue("leadId", option ? option.value : "")
                        }
                        value={
                          allLeads.find((opt: OptionType) => opt.value === values.leadId) || null
                        }
                      />
                      <ErrorMessage name="leadId" component="div" className="invalid-feedback" />
                    </Col>
                  </Form.Group>
                )}
                <Form.Group as={Row} controlId="from">
                  <Form.Label column xs={3}>From *</Form.Label>
                  <Col xs={9}>
                    <Field name="from" type="email" className="form-control" />
                    <ErrorMessage name="from" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="to">
                  <Form.Label column xs={3}>To *</Form.Label>
                  <Col xs={9}>
                    <Field name="to" type="email" className="form-control" />
                    <ErrorMessage name="to" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="subject">
                  <Form.Label column xs={3}>Subject *</Form.Label>
                  <Col xs={9}>
                    <Field name="subject" type="text" className="form-control" />
                    <ErrorMessage name="subject" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group as={Row} controlId="body">
                  <Form.Label column xs={3}>Body *</Form.Label>
                  <Col xs={9}>
                    <ReactQuill
                      value={values.body ?? ""}
                      onChange={(value) => setFieldValue("body", value)}
                      className={errors.body && touched.body ? "is-invalid" : ""}
                    />
                    <ErrorMessage name="body" component="div" className="invalid-feedback" />
                  </Col>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Uploaded Files</Form.Label>
                  <div>
                    {documents.map((doc) => renderDocumentPreview(doc))}
                  </div>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Add Files</Form.Label>
                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={(e) => {
                      const files = Array.from(e.currentTarget.files || []);
                      if (values.id) {
                        handleFileUpload(values.id, files);
                      }
                    }}
                    className="form-control"
                  />
                  {isUploadingFiles && <LoadingSpinner text="Uploading files..." />}
                </Form.Group>
              </Stack>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" type="submit" disabled={isSubmitting} className="me-2">
                {isSubmitting ? <LoadingSpinner text="Updating log..." /> : "Update log"}
              </Button>
              <Button variant="secondary" onClick={() => onClose(false)}>
                Cancel
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};